import React, { useState, useEffect } from "react";
import * as Mui from "@mui/material";
import {
  AddRoad,
  Payment,
  RemoveCircleOutline,
  VideoLabel,
  ViewCompactAlt,
  Wallet,
} from "@mui/icons-material";
import { themes } from "services/constants";
import { BackArrow } from "components/CustomComponents/BackArrow";
import { CustomAutoComplete } from "components/CustomComponents/CustomAutoComplete";
import { summaryDetails } from "pages/PayRoll/PayRollHeaders";
import { setGlobalSearchValue } from "services/Redux/userToken";
import { useSelector } from "react-redux";
import { CostStructure } from "./CostStructure/PayrunCycleDetails";
import { AttendanceTimeLine } from "./AttendanceTimeLine";
import { useDispatch } from "react-redux";
import { showToast } from "services/utils/Status";
import { amountNumberFormat } from "services/utils/numberFormat";
import { useFetch } from "services/hooks/useFetch";
import { usePayrollCycleRun } from "./PayrollCycleRunContext";
import { CustomButtonWithBorder } from "components/CustomComponents/CustomButton";
import comLogo from "../../../../../assets/img/brand/company_logo.png";
import { customAPI } from "services/utils/CustomAPI";
import { CustomButton } from "components/CustomComponents";
import { ConfirmationDialog } from "components/CustomComponents/ConfirmationDialog";

export const ViewPayRun = ({ companyId, companyName, viewPayrunDetails }) => {
  const { domain } = useSelector((state) => state.tokenReducer);
  const {
    payScheduleData,
    payScheduleId,
    setViewPayRunScreen,
    setActiveTab,
    isProcessing,
    processingMessage,
    paymentBasedOn,
    setRerunLoader,
    socketCompleted,
    recallSummaryApi,
    setRecallSummaryApi,
    setSocketCompleted,
  } = usePayrollCycleRun();
  const { result: summaryData, setLoad: recallApi } = useFetch(
    `${domain}payroll/operations/payrollprocessing/payrollruncycle/payrun-summary/?pay_schedule_id=${payScheduleId}`
  );
  const [summaryName, setSummaryName] = useState("Cost Structure");
  const [downloadReportDetails, setDownloadReportDetails] = useState(false);
  const [rerunPayrun, setRerunPayrun] = useState(false);
  const [recallEmployee, setRecallEmployee] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const dispatch = useDispatch();
  const handleSummaryDetails = (event, data) => {
    if (data) {
      setSummaryName(data?.label);
      dispatch(setGlobalSearchValue(""));
    }
  };

  const renderComponent = () => {
    switch (summaryName) {
      case "Cost Structure":
        return (
          <CostStructure
            companyId={companyId}
            recallEmployee={recallEmployee}
            setRecallEmployee={setRecallEmployee}
          />
        );
      case "Attendance Timeline":
        return (
          <AttendanceTimeLine
            viewPayrunDetails={viewPayrunDetails}
            companyName={companyName}
            setDownloadReportDetails={setDownloadReportDetails}
            downloadReportDetails={downloadReportDetails}
            summaryName={summaryName}
          />
        );
      default:
        break;
    }
  };

  const payrollData = [
    {
      id: 1,
      icon: Wallet,
      label: "Payroll Cost",
      value: summaryData ? amountNumberFormat(summaryData?.payroll_cost) : 0,
      bgColor: "#09bd710d",
      iconBgColor: "#09bd711a",
      iconBorderColor: "#09BD71",
      iconColor: "#09BD71",
    },
    {
      id: 2,
      icon: RemoveCircleOutline,
      label: "Total deductions",
      value: summaryData ? amountNumberFormat(summaryData?.total_deduction) : 0,
      bgColor: "#3c73e70d",
      iconBgColor: "#3c73e71a",
      iconBorderColor: "#3C73E7",
      iconColor: "#3C73E7",
    },
    {
      id: 3,
      icon: Payment,
      label: "Net Salary",
      value: summaryData ? amountNumberFormat(summaryData?.net_salary) : 0,
      bgColor: "#c411110d",
      iconBgColor: "#bd09091a",
      iconBorderColor: "#D71515",
      iconColor: "#D71515",
    },
    {
      id: 4,
      icon: AddRoad,
      label: "Additional Deduction",
      value: summaryData
        ? amountNumberFormat(summaryData?.additional_deduction)
        : 0,
      bgColor: "#09bd710d",
      iconBgColor: "#09bd711a",
      iconBorderColor: "#09BD71",
      iconColor: "#09BD71",
    },
    {
      id: 5,
      icon: VideoLabel,
      label: "Advances",
      value: summaryData ? amountNumberFormat(summaryData.advance || 0) : 0,
      bgColor: "#3c73e70d",
      iconBgColor: "#3c73e71a",
      iconBorderColor: "#3C73E7",
      iconColor: "#3C73E7",
    },
    {
      id: 6,
      icon: ViewCompactAlt,
      label: "Net Pay",
      value: summaryData ? amountNumberFormat(summaryData.net_pay) : 0,
      bgColor: "#c411110d",
      iconBgColor: "#bd09091a",
      iconBorderColor: "#D71515",
      iconColor: "#D71515",
    },
  ];

  useEffect(() => {
    if (recallSummaryApi) {
      recallApi(true);
      setRecallSummaryApi(false);
    }
  }, [recallApi, recallSummaryApi, setRecallSummaryApi]);

  useEffect(() => {
    const handleRerunPayrun = async () => {
      setRerunLoader(true);
      const response = await customAPI(
        `${domain}payroll/operations/payrollprocessing/payrollruncycle/intiate_payrun/`,
        "POST",
        {
          pay_schedule_id: payScheduleId,
          payment_based_on: paymentBasedOn,
          use_background_task: false,
          delete_manual_data: true,
        }
      );

      if (response.ok) {
        setShowConfirmationDialog(false);
        recallApi(true);
        showToast("success", response?.responseDetails?.message);
        setRerunPayrun(false);
        setRerunLoader(false);
        setRecallEmployee(true);
      } else {
        showToast("error", response?.message);
        setRerunPayrun(false);
        setRerunLoader(false);
      }
    };

    if (rerunPayrun) {
      handleRerunPayrun();
    }
  }, [
    domain,
    payScheduleId,
    paymentBasedOn,
    recallApi,
    rerunPayrun,
    setRerunLoader,
  ]);

  const handleApproveRejectReopen = async (status, activeTab) => {
    const response = await customAPI(
      `${domain}payroll/operations/payrollprocessing/payrollruncycle/payschedules/`,
      "PUT",
      {
        pay_schedule_id: payScheduleId,
        status: status,
      }
    );

    if (response.ok) {
      showToast("success", response?.responseDetails?.message);
      setActiveTab(activeTab);
      setViewPayRunScreen(false);
    } else {
      showToast("error", response?.message);
    }
  };

  const handleRecalculate = async () => {
    setRerunLoader(true);
    const response = await customAPI(
      `${domain}payroll/operations/payrollprocessing/payrollruncycle/intiate_payrun/`,
      "POST",
      {
        pay_schedule_id: payScheduleId,
        payment_based_on: paymentBasedOn,
        use_background_task: false,
        delete_manual_data: false,
      }
    );

    if (response.ok) {
      showToast("success", response?.responseDetails?.message);
      setRecallEmployee(true);
      recallApi(true);
      setRerunLoader(false);
    } else {
      showToast("error", response?.message);
    }
  };

  const handleCancelClose = () => {
    setShowConfirmationDialog(false);
  };

  return (
    <>
      <React.Fragment>
        <ConfirmationDialog
          open={showConfirmationDialog}
          onClose={handleCancelClose}
          onConfirm={() => setRerunPayrun(true)}
          title={"Re Run"}
          message={
            "Once you rerun the payrun, all updated earning details and manual adjustments will be lost. Are you sure you want to proceed?"
          }
          confirmText={"Confirm"}
          cancelText="Cancel"
        />
        <Mui.Grid
          container
          alignItems="center"
          justifyContent="space-between"
          mb={3}
          flexWrap="wrap"
        >
          <Mui.Grid item>
            <Mui.Stack direction="Row">
              <BackArrow
                actionFunction={() => {
                  setViewPayRunScreen(false);
                  dispatch(setGlobalSearchValue(""));
                  setSocketCompleted(false);
                }}
              />
              <Mui.Box>
                <Mui.Typography variant="h6" fontWeight={600}>
                  {payScheduleData?.name}
                </Mui.Typography>
                <Mui.Typography variant="body2" color="text.secondary">
                  Period:{" "}
                  {`${payScheduleData?.start_date} - ${payScheduleData?.end_date}`}
                </Mui.Typography>
              </Mui.Box>
            </Mui.Stack>
          </Mui.Grid>

          <Mui.Grid
            item
            sx={{ display: "flex", alignItems: "center", gap: 1, mr: 2 }}
          >
            <Mui.Chip
              label={payScheduleData?.status}
              sx={{
                fontWeight: 600,
                color:
                  payScheduleData?.status === "Draft"
                    ? "#a89ef3"
                    : payScheduleData?.status === "Submitted"
                    ? "#7e8180"
                    : "#2E7D32",
                border: `1px solid ${
                  payScheduleData?.status === "Draft"
                    ? "#a89ef3"
                    : payScheduleData?.status === "Submitted"
                    ? "#7e8180"
                    : "#2E7D32"
                }`,
                backgroundColor: "transparent",
              }}
            />
          </Mui.Grid>
        </Mui.Grid>
        <Mui.Card
          sx={{
            mb: 3,
            boxShadow: "none",
            border: "1px solid #e0e0e0",
            overflow: "visible",
          }}
        >
          <Mui.CardContent
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              overflow: "visible",
            }}
          >
            <Mui.Box sx={{ overflow: "visible" }}>
              <Mui.FormControl
                size="small"
                sx={{
                  minWidth: 250,
                  overflow: "visible",
                  position: "relative",
                }}
              >
                <CustomAutoComplete
                  dataList={summaryDetails}
                  selectedValue={summaryName}
                  updateDataFunction={handleSummaryDetails}
                  placeHolder="Select the summary"
                  fullWidth
                  popperSx={{ zIndex: 9999 }}
                />
              </Mui.FormControl>
            </Mui.Box>
            <Mui.Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {{
                Inprogress: (
                  <>
                    <CustomButtonWithBorder
                      actionName="Re Initiate"
                      actionFuntion={() => {
                        setShowConfirmationDialog(true);
                      }}
                    />
                    <CustomButtonWithBorder
                      actionName="Re calculate"
                      actionFuntion={() => {
                        handleRecalculate();
                      }}
                    />
                    <CustomButton
                      actionName="Submit"
                      actionFuntion={() => {
                        handleApproveRejectReopen("Submitted", 1);
                      }}
                    />
                  </>
                ),

                Submitted: (
                  <>
                    <CustomButtonWithBorder
                      actionName="Approve"
                      actionFuntion={() => {
                        handleApproveRejectReopen("Approved", 2);
                      }}
                    />
                    <CustomButtonWithBorder
                      actionName="Reject"
                      handleColor={true}
                      actionFuntion={() => {
                        handleApproveRejectReopen("Rejected", 0);
                      }}
                    />
                  </>
                ),

                Approved: (
                  <>
                    {/* <CustomButtonWithBorder
                      actionName="Disburse"
                      actionFuntion={() => {
                        handleApproveRejectReopen("Disburse", 2);
                      }}
                    /> */}
                    <CustomButton
                      actionName="Reopen"
                      actionFuntion={() => {
                        handleApproveRejectReopen("Reopened", 0);
                      }}
                    />
                  </>
                ),
              }[payScheduleData?.status] || null}
            </Mui.Box>
          </Mui.CardContent>
        </Mui.Card>
        {socketCompleted && (
          <Mui.Box sx={{ flexGrow: 1, overflow: "auto", pt: 0, mb: 2 }}>
            <Mui.Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 4,
              }}
            >
              {payrollData.map((item) => {
                const IconComponent = item.icon;
                return (
                  <Mui.Card
                    key={item.id}
                    sx={{
                      width: 200,
                      height: 123,
                      bgcolor: themes?.whiteColor,
                      borderRadius: 1,
                      boxShadow: "0px 4px 4px rgba(0,0,0,0.25)",
                      border: `0.5px solid ${item.iconBorderColor}`,
                    }}
                  >
                    <Mui.CardContent
                      sx={{
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                      }}
                    >
                      <Mui.Box
                        sx={{
                          width: 35,
                          height: 35,
                          bgcolor: item.iconBgColor,
                          border: `0.5px solid ${item.iconBorderColor}`,
                          borderRadius: 1,
                          boxShadow: "0px 1px 2px rgba(0,0,0,0.25)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 1.5,
                        }}
                      >
                        <IconComponent
                          width={16}
                          height={16}
                          style={{ color: item.iconColor, padding: 2 }}
                        />
                      </Mui.Box>
                      <Mui.Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          mb: "auto",
                        }}
                      >
                        {item.label}
                      </Mui.Typography>
                      <Mui.Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: "#32325D",
                        }}
                      >
                        {item.value}
                      </Mui.Typography>
                    </Mui.CardContent>
                  </Mui.Card>
                );
              })}
            </Mui.Box>
          </Mui.Box>
        )}

        {isProcessing && (
          <>
            <Mui.Box
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                backgroundColor: "rgba(0,0,0,0.15)",
                zIndex: (theme) => theme.zIndex.drawer + 999,
              }}
            />

            <Mui.Box
              sx={{
                position: "relative",
                zIndex: (theme) => theme.zIndex.drawer + 1000,
                display: "flex",
                flexDirection: "row",
                alignItems: "stretch",
                gap: 2,
                mb: 3,
              }}
            >
              <Mui.Box
                sx={{
                  width: 80,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Mui.Box
                  sx={{
                    width: 55,
                    height: 55,
                    borderRadius: "50%",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Mui.CircularProgress
                    size={55}
                    thickness={4}
                    sx={{
                      color: "#4cd964",
                      position: "absolute",
                      animationDuration: "1.2s",
                    }}
                  />

                  <img
                    src={comLogo}
                    alt="Company Logo"
                    style={{
                      width: 28,
                      height: 28,
                      objectFit: "contain",
                      zIndex: 10,
                    }}
                  />
                </Mui.Box>

                <Mui.Typography
                  variant="caption"
                  sx={{
                    color: "#00a65a",
                    fontWeight: 600,
                    mt: 1,
                  }}
                >
                  Processing
                </Mui.Typography>
              </Mui.Box>

              <Mui.Box
                sx={{
                  flexGrow: 1,
                  bgcolor: "#fffdd7",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  px: 3,
                }}
              >
                <Mui.Typography
                  variant="body2"
                  sx={{
                    color: "#444",
                    fontSize: 15,
                    fontWeight: 500,
                  }}
                >
                  {processingMessage}
                </Mui.Typography>
              </Mui.Box>
            </Mui.Box>
          </>
        )}
        <Mui.Grid container>{renderComponent()}</Mui.Grid>
      </React.Fragment>
    </>
  );
};
